import React, { useState, useEffect } from 'react';
import { SFConnection } from 'common/sf-connector';
import { DataTable, DataTableColumn } from '@salesforce/design-system-react';
import { Button } from '@salesforce/design-system-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import DefaultTaskEngine from 'tasks/task-engine';
import { useTranslation } from 'react-i18next';
import { CQAppConstant } from "app-constants";
import DefaultStorage from 'common/storage';
import { getProcessType, getUpgradedType } from 'common/module.utils';

declare module 'jspdf' {
    interface jsPDF {
        autoTable: any;
    }
}
export interface CQSummaryReportProps {
    isViewHistoryClicked?: boolean;
    currentModule: string;
    selectedVersion?: string;
}

/**
 * CQSummaryReport component fetches and displays a summary of installed and updated packages.
 * It provides functionality to download the summary as a PDF document.
 * 
 * @function fetchData
 * Fetches installed and latest packages from Salesforce and updates the state.
 *
 * @function getPackageMapping
 * Generates a mapping of installed packages to their corresponding updated packages.
 
 * @function handleDownload
 * Generates and downloads a CQ Summary Report as a PDF document.
 *
 * @function summaryHeader
 * Renders the summary header.
 *
 * @function summaryDownloadButton
 * Renders the download button.
 *
 * @function summaryTable
 * Renders the summary table.
 */
function CQSummaryReport(props: CQSummaryReportProps) {
    const {t} = useTranslation();
    const [installedPackages, setInstalledPackages] = useState<{ name: string; fullName: string }[]>([]);
    const [afterPackages, setAfterPackages] = useState<{ name: string; fullName: string }[]>([]);
    const [packageNameAndVersion, setPackageNameAndVersion] = useState<string>();


    /**
     * Fetches data related to installed and latest packages from Salesforce.
     */
    async function fetchData() {
        const installedPackages = await SFConnection.getInstalledPackages();
        const corePackage = installedPackages.find((pkg) => pkg.SubscriberPackage.Name === "ComplianceQuest");
        let coreVersion = "Unknown Package Version";
        let packageBeforeInstallation = [];
        let packageAfterInstallation = [];
        if (corePackage?.SubscriberPackageVersion?.Name) {
            coreVersion = corePackage.SubscriberPackageVersion.Name;
        }
        setPackageNameAndVersion(coreVersion);
        await DefaultStorage.restore();
        const historyData = await DefaultTaskEngine.readInstalledPackagesHistory();
        if (historyData) {
            const parsedHistoryData = typeof historyData === 'string' ? JSON.parse(historyData) : historyData;
            packageBeforeInstallation = parsedHistoryData.before;
            packageAfterInstallation = parsedHistoryData.after;
        }

        setInstalledPackages(packageBeforeInstallation);
        setAfterPackages(packageAfterInstallation);
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Generates a mapping of installed packages to their corresponding updated packages.
     *
     * @returns An array of objects where each object contains:
     */

    const getPackageMapping = () => {
        return installedPackages.map((pkg) => {
            const matchingAfterPackage = afterPackages.find((afterPkg) => afterPkg.name === pkg.name);
            return {
                before: pkg.fullName,
                after: matchingAfterPackage ? matchingAfterPackage.fullName : "No Update"
            };
        });
    };

    /**
     * Generates and downloads a CQ Summary Report as a PDF document.
     * @function handleDownload
     * @returns {void}
     */
    const handleDownload = () => {
        const pdfTitle = packageNameAndVersion || CQAppConstant.NO_PACKAGE;
        const pdfMsg = getUpgradedType(props.currentModule);
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text("CQ Summary Report", 105, 20, { align: "center" }); // Centered title at coordinates{x:105, y:20)
        doc.setFontSize(12);
        doc.text(t("summary_heading", { type: pdfMsg, packageName: pdfTitle }), 10, 30); //Place the text at coordinates {x:10, y:30}
        doc.autoTable({
            head: [['From', 'To']],
            body: getPackageMapping().map((pkg) => [pkg.before, pkg.after]),
            startY: 40, // Table starts at y-coordinate 40
            styles: { fillColor: [255, 255, 255] },
            headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] }
        });
        const now = new Date();
        const fileName = `${packageNameAndVersion}_${now.toLocaleDateString()}_${now.toLocaleTimeString()}`.split(" ").join("_")
        doc.save(fileName); // Save the PDF with the given filename
    };

    /**
     * Renders the summary header.
     * 
     * @function summaryHeader
     * @returns {JSX.Element} The summary header component.
     */
    function summaryHeader() {
        const packageName = props.selectedVersion || packageNameAndVersion;
        const mainMessage = getUpgradedType(props.currentModule);
        const subMessage = getProcessType(props.currentModule);
        return summaryTitle(packageName, mainMessage, subMessage);
    }

    /**
     * Renders the summary title section for the report, including a main heading and a subheading.
     *
     * @param packageName - The name of the package to display in the main heading. Can be undefined.
     * @param mainMessage - The main message or type to be shown in the heading.
     * @param subMessage - The sub-message or upgrade type to be shown in the subheading.
     * @returns A JSX element containing the summary title and subheading, with conditional rendering based on the view history state.
     */
    function summaryTitle(packageName: string | undefined, mainMessage: string, subMessage: string) {
        let isHideHeader = props.isViewHistoryClicked;
        let className = isHideHeader ? 'slds-text-heading_medium' : 'slds-text-heading_small slds-p-top_small';
        return (<div className=" slds-text-align_left">
            {isHideHeader ? <></> : <div className="slds-text-heading_medium">{t("summary_heading", { type: mainMessage, packageName })}</div>}
            <div className={className}>{t("summary_sub_heading", { upgradeType: subMessage })}</div>
        </div>);
    }

    /**
     * Renders the download button.
     * 
     * @function summaryDownloadButton
     * @returns {JSX.Element} The download button component.
     */
    function summaryDownloadButton() {
        return (<div className=" slds-text-align_right">
            <Button className="slds-p-right_small" label="Download as PDF" variant="base" onClick={handleDownload} />
        </div>);
    }

    /**
     * Renders the summary table.
     * 
     * @function summaryTable
     * @returns {JSX.Element} The summary table component.
     */
    function summaryTable() {
        return (<div>
            <div style={{ overflow: 'auto' }} >
                <div className='slds-m-bottom_x-small'></div>
                <DataTable columnBordered fixedLayout items={getPackageMapping()}>
                    <DataTableColumn key="before" label="From" property="before" />
                    <DataTableColumn key="after" label="To" property="after" />
                </DataTable>
            </div>
        </div>);
    }

    return (
        <div>
            {summaryHeader()}
            {summaryDownloadButton()}
            {summaryTable()}
        </div>
    );
}

export default CQSummaryReport;